import jsPDF from 'jspdf';

const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const BRAND = {
  navy: [7, 31, 84],
  blue: [30, 96, 205],
  ice: [244, 248, 253],
  iceStrong: [235, 242, 251],
  border: [207, 220, 237],
  text: [26, 37, 55],
  muted: [91, 108, 132],
  white: [255, 255, 255],
};

const formatDateBr = (value) => {
  const raw = String(value || '').slice(0, 10);
  if (!raw) return '';
  const [year, month, day] = raw.split('-');
  return year && month && day ? `${day}/${month}/${year}` : raw;
};

const safeFilePart = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^A-Za-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .toLowerCase();

export async function generateSecretariaAttendanceReport({ history = [], classes = [], courses = [], filters = {} }) {
  const month = Number(filters.month || 1);
  const year = Number(filters.year || new Date().getFullYear());
  const responsible = String(filters.responsible || '').trim();
  const courseId = String(filters.courseId || '').trim();
  const classById = new Map(classes.map((item) => [String(item.id), item]));
  const courseById = new Map(courses.map((item) => [String(item.id), item]));

  if (!responsible) {
    return { error: 'Selecione o responsável antes de gerar o relatório para a Secretaria.' };
  }

  const sessions = [...history]
    .filter((session) => {
      const rawDate = String(session?.date || '').slice(0, 10);
      if (!rawDate) return false;
      const date = new Date(`${rawDate}T00:00:00`);
      return date.getFullYear() === year
        && date.getMonth() + 1 === month
        && String(session?.responsibleName || '').trim() === responsible
        && (!courseId || String(classById.get(String(session?.classId || ''))?.courseId || '') === courseId);
    })
    .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')) || Number(a.id || 0) - Number(b.id || 0));

  if (!sessions.length) {
    return { error: 'Nenhuma frequência encontrada para o mês e responsável selecionados.' };
  }

  const rows = sessions.map((session) => {
    const klass = classById.get(String(session.classId || ''));
    const className = klass?.name || session.className || 'Turma não informada';
    const courseName = courseById.get(String(klass?.courseId || ''))?.name || 'Sem curso';
    const theme = String(session.lessonTheme || session.theme || '').trim() || 'Tema não informado';
    const objective = String(session.objective || '').trim() || '—';
    const participants = (session.entries || []).filter((entry) => entry.status === 'PRESENT').length;
    return { session, className, courseName, theme, objective, participants };
  });

  const classNames = Array.from(new Set(rows.map((row) => row.className).filter(Boolean)));
  const courseNames = Array.from(new Set(rows.map((row) => row.courseName).filter(Boolean)));
  const themes = Array.from(new Set(rows.map((row) => row.theme).filter((value) => value && value !== 'Tema não informado')));
  const totalParticipants = rows.reduce((sum, row) => sum + row.participants, 0);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 38;
  const contentWidth = pageWidth - margin * 2;
  const descriptionWidth = Math.round(contentWidth * 0.48);
  const participantsWidth = 94;
  const objectiveWidth = contentWidth - descriptionWidth - participantsWidth;
  const lineHeight = 11.5;
  const headerHeight = 64;

  const drawBrandHeader = (compact = false) => {
    const bandHeight = compact ? 50 : headerHeight;
    doc.setFillColor(...BRAND.navy);
    doc.rect(0, 0, pageWidth, bandHeight, 'F');

    doc.setFillColor(...BRAND.blue);
    doc.rect(0, bandHeight - 4, pageWidth, 4, 'F');

    doc.setTextColor(...BRAND.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(compact ? 16 : 21);
    doc.text(
      'Relatório Mensal de Frequência',
      pageWidth / 2,
      compact ? 30 : 38,
      { align: 'center' },
    );
  };

  const drawInfoCard = ({ label, value, x, y, width, accent = false }) => {
    const height = 44;
    doc.setFillColor(...(accent ? BRAND.iceStrong : BRAND.ice));
    doc.setDrawColor(...BRAND.border);
    doc.setLineWidth(0.6);
    doc.roundedRect(x, y, width, height, 7, 7, 'FD');

    if (accent) {
      doc.setFillColor(...BRAND.blue);
      doc.roundedRect(x, y, 4, height, 2, 2, 'F');
    }

    doc.setTextColor(...BRAND.muted);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(String(label || '').toUpperCase(), x + 10, y + 13);

    doc.setTextColor(...BRAND.text);
    doc.setFont('helvetica', accent ? 'bold' : 'normal');
    doc.setFontSize(accent ? 12 : 9.5);
    const maxWidth = width - 20;
    const lines = doc.splitTextToSize(String(value || '—'), maxWidth).slice(0, 2);
    doc.text(lines, x + 10, y + 29, { lineHeightFactor: 1.12 });
    return height;
  };

  const drawTableHeader = (y) => {
    doc.setFillColor(...BRAND.navy);
    doc.setDrawColor(...BRAND.navy);
    doc.setLineWidth(0.7);
    doc.rect(margin, y, descriptionWidth, 33, 'FD');
    doc.rect(margin + descriptionWidth, y, objectiveWidth, 33, 'FD');
    doc.rect(margin + descriptionWidth + objectiveWidth, y, participantsWidth, 33, 'FD');

    doc.setTextColor(...BRAND.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.3);
    doc.text('ATIVIDADE REALIZADA', margin + descriptionWidth / 2, y + 20, { align: 'center' });
    doc.text('OBJETIVO DA ATIVIDADE', margin + descriptionWidth + objectiveWidth / 2, y + 20, { align: 'center' });
    doc.text(['Nº DE', 'PARTICIPANTES'], margin + descriptionWidth + objectiveWidth + participantsWidth / 2, y + 12, { align: 'center' });
    return y + 33;
  };

  drawBrandHeader(false);

  let y = headerHeight + 16;
  const gap = 10;
  const quarter = (contentWidth - gap * 3) / 4;
  const half = (contentWidth - gap) / 2;

  drawInfoCard({ label: 'Mês de referência', value: `${monthNames[month - 1]}/${year}`, x: margin, y, width: quarter });
  drawInfoCard({ label: 'Responsável', value: responsible, x: margin + quarter + gap, y, width: quarter * 2 + gap });
  drawInfoCard({ label: 'Total de participantes', value: String(totalParticipants), x: margin + quarter * 3 + gap * 3, y, width: quarter, accent: true });

  y += 54;
  drawInfoCard({ label: 'Curso', value: courseNames.join(' / ') || 'Sem curso', x: margin, y, width: half });
  drawInfoCard({ label: 'Turmas', value: classNames.join(' / '), x: margin + half + gap, y, width: half });

  y += 54;
  drawInfoCard({ label: 'Conteúdos', value: themes.length ? themes.join(' / ') : 'Temas registrados nas frequências do período', x: margin, y, width: contentWidth });

  y += 58;
  y = drawTableHeader(y);

  rows.forEach((row, index) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);

    const themeLines = doc.splitTextToSize(row.theme, descriptionWidth - 22);
    const objectiveLines = doc.splitTextToSize(row.objective, objectiveWidth - 22);
    const descriptionLines = [
      formatDateBr(row.session.date),
      `Curso: ${row.courseName} • Turma: ${row.className}`,
      '',
      ...themeLines,
    ];
    const contentLines = Math.max(descriptionLines.length, objectiveLines.length, 2);
    const rowHeight = Math.max(62, 18 + contentLines * lineHeight);

    if (y + rowHeight > pageHeight - 40) {
      doc.addPage();
      drawBrandHeader(true);
      y = drawTableHeader(62);
    }

    const fill = index % 2 === 0 ? BRAND.white : BRAND.ice;
    doc.setFillColor(...fill);
    doc.setDrawColor(...BRAND.border);
    doc.setLineWidth(0.6);
    doc.rect(margin, y, descriptionWidth, rowHeight, 'FD');
    doc.rect(margin + descriptionWidth, y, objectiveWidth, rowHeight, 'FD');
    doc.rect(margin + descriptionWidth + objectiveWidth, y, participantsWidth, rowHeight, 'FD');

    doc.setTextColor(...BRAND.text);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.8);
    doc.text(descriptionLines[0], margin + 10, y + 15);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...BRAND.muted);
    doc.text(descriptionLines[1], margin + 10, y + 29);

    doc.setTextColor(...BRAND.text);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.2);
    if (themeLines.length) doc.text(themeLines, margin + 10, y + 47, { lineHeightFactor: 1.22 });
    doc.text(objectiveLines, margin + descriptionWidth + 10, y + 22, { lineHeightFactor: 1.22 });

    const numberX = margin + descriptionWidth + objectiveWidth + participantsWidth / 2;
    const numberY = y + rowHeight / 2 + 4;
    doc.setFillColor(...(row.participants > 0 ? BRAND.iceStrong : BRAND.ice));
    doc.circle(numberX, numberY - 3, 15, 'F');
    doc.setTextColor(...(row.participants > 0 ? BRAND.navy : BRAND.muted));
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(String(row.participants), numberX, numberY, { align: 'center' });

    y += rowHeight;
  });

  const totalPages = doc.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    doc.setPage(pageNumber);
    doc.setDrawColor(...BRAND.border);
    doc.line(margin, pageHeight - 26, pageWidth - margin, pageHeight - 26);

    doc.setTextColor(...BRAND.muted);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.2);
    doc.text('Intelligent BPM  •  Nash Tecnologia', margin, pageHeight - 13);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...BRAND.navy);
    doc.text(`Página ${pageNumber} de ${totalPages}`, pageWidth - margin, pageHeight - 13, { align: 'right' });
  }

  const fileResponsible = safeFilePart(responsible) || 'responsavel';
  doc.save(`relatorio-secretaria-frequencia-${String(month).padStart(2, '0')}-${year}-${fileResponsible}.pdf`);
  return { error: null, sessions: rows.length, participants: totalParticipants };
}
